import { Box, Button, IconButton, Tab, Tabs, Typography } from '@mui/material';
import { ArrowBack, ContentCopy } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { useRememberedScroll } from '@frontend/features/common/hooks/useRememberedScroll';
import { dashboardReturnUrl } from '@frontend/features/dashboard/navigationMemory';
import { routes } from '@frontend/router/routes';
import EditorContextProvider from '../hooks/useEditor';
import { CaseMobileCommunications } from './CaseMobileCommunications';
import { CaseSidebar } from './CaseSidebar';
import { CaseContentHistory } from './CaseContentHistory';
import { CaseAgentSelectorMobile } from './CaseAgentSelectorMobile';
import { CaseRenameModal } from './CaseRenameModal';
import { DraftProvider } from '../context/DraftContext';
import { useCaseContext } from '../context';

const CaseViewMobileContent = () => {
  const { t } = useTranslation();
  const { myProfile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = location.hash === '#history' ? 'history' : 'info';
  const context = useCaseContext();
  const { caseData } = context;
  const scrollRef = useRememberedScroll(Boolean(caseData) && activeTab === 'info', 'case-info');

  return (
    <Box sx={{ height: '100%', minWidth: 0, display: 'flex', flexDirection: 'column',
      '& input, & textarea, & .MuiSelect-select': { fontSize: 16 },
      '& .MuiIconButton-root, & .MuiButton-root': { minHeight: 44, minWidth: 44 } }}>
      <Box p={2} pb={1} sx={{ flexShrink: 0 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <Button component={Link} to={dashboardReturnUrl(myProfile?.id, routes.dashboard.cases)} startIcon={<ArrowBack />}>
            {t('cases.list.title')}
          </Button>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="body2">#{caseData?.id}</Typography>
            <IconButton aria-label={t('cases.view.copyCaseNumber')} disabled={!caseData} onClick={context.onCopyCaseNumber}>
              <ContentCopy fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        <Button onClick={context.onEditCaseName} color="inherit" sx={{ textAlign: 'left', textTransform: 'none', px: 0, width: '100%', justifyContent: 'flex-start' }}>
          <Typography component="h1" variant="h6" sx={{ overflowWrap: 'anywhere' }}>{caseData?.name}</Typography>
        </Button>
        <CaseMobileCommunications caseData={caseData} />
      </Box>
      <Tabs value={activeTab} variant="fullWidth" aria-label={t('cases.view.case')}
        onChange={(_, tab: string) => navigate({ pathname: location.pathname, search: location.search, hash: tab === 'history' ? '#history' : '' }, { replace: true })}>
        <Tab value="info" label={t('cases.view.tabInfo')} id="case-info-tab" aria-controls="case-info-panel" />
        <Tab value="history" label={t('cases.view.tabHistory')} id="case-history-tab" aria-controls="case-history-panel" />
      </Tabs>
      {activeTab === 'info' ? (
        <Box ref={scrollRef} role="tabpanel" id="case-info-panel" aria-labelledby="case-info-tab" sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <CaseSidebar isMobile hideHeader
            onEditCaseName={context.onEditCaseName} onCopyCaseNumber={context.onCopyCaseNumber}
            onChangeStatus={context.onChangeStatus} onChangeWorkflow={context.onChangeWorkflow}
            onChangeIAEnabled={context.onChangeIAEnabled} onChangeStep={context.onChangeStep}
            onStepInstructionsOpen={context.onStepInstructionsOpen} onClientSelectorOpen={context.onClientSelectorOpen}
            onOrganizationEditOpen={context.onOrganizationEditOpen} onAgencyEditOpen={context.onAgencyEditOpen}
            onTeamSelectorOpen={context.onTeamSelectorOpen} onAgentSelectorOpen={context.onAgentSelectorOpen}
            onAssignToMe={context.onAssignToMe} onSaveLegacySaleOrder={context.onSaveLegacySaleOrder}
            onSaveLegacyReference={context.onSaveLegacyReference} onRemoveRelation={context.onRemoveRelation}
            onLinkCaseOpen={context.onLinkCaseOpen} />
        </Box>
      ) : (
        <Box role="tabpanel" id="case-history-panel" aria-labelledby="case-history-tab" sx={{ flex: 1, minHeight: 0 }}>
          <CaseContentHistory channel={null} isMobile />
        </Box>
      )}
      {caseData && context.isAgentEditOpen && <CaseAgentSelectorMobile key={caseData.id}
        caseData={caseData} onClose={context.toggleAgentSelector} onSelect={context.onChangeAgent} />}
      {caseData && context.isCaseNameEditOpen && <CaseRenameModal
        open={context.isCaseNameEditOpen} onClose={context.toggleEditCaseName}
        onSave={context.onChangeName} currentName={caseData.name} />}
    </Box>
  );
};

export const CaseViewMobile = () => {
  const { id } = useParams();
  const { myProfile } = useAuth();
  const key = `case-mobile:${myProfile?.id}:${id}`;
  return <EditorContextProvider key={key}>
    <DraftProvider caseId={key}><CaseViewMobileContent /></DraftProvider>
  </EditorContextProvider>;
};
